import React, { ForwardedRef, forwardRef } from 'react'
import cn from 'classnames'
import { useFormControlContext } from './form-control-context'
import styles from './form.module.scss'

interface InputProps {
  as?: 'input' | 'textarea'
  className?: string
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onPaste?: React.ClipboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  placeholder?: string
  value?: string | number
  type?: string
  // Id of the element that provides an additional description for the input field
  ariaDescribedBy?: string

  // When using Input inside a FormControl group, these props may be omitted.
  isDisabled?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  name?: string
  id?: string
  maxLength?: number
  autoFocus?: boolean
}

export const Input = forwardRef((props: InputProps, ref) => {
  const { nameContext, idContext, isDisabledContext, isRequiredContext, isInvalidContext } = useFormControlContext()

  const {
    as = 'input',
    className,
    isDisabled = isDisabledContext,
    isInvalid = isInvalidContext,
    isRequired = isRequiredContext,
    name = nameContext,
    id = idContext,
    ariaDescribedBy,
    onFocus,
    onBlur,
    onChange,
    onPaste,
    placeholder,
    value,
    type,
    maxLength,
    autoFocus,
  } = props

  const inputProps = {
    ...(isDisabled ? { disabled: true } : {}),
    ...(isInvalid ? { 'aria-invalid': true } : {}),
    ...(isRequired ? { 'aria-required': true } : {}),
    ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
    className: cn(styles.input, className, isInvalid && styles.inputError, isDisabled && styles.inputDisabled),
    id,
    placeholder,
    name,
    value,
    type,
    onFocus,
    onBlur,
    onChange,
    onPaste,
    maxLength,
    autoFocus,
  }

  if (as === 'textarea') {
    return <textarea {...inputProps} ref={ref as ForwardedRef<HTMLTextAreaElement>} />
  }

  return <input {...inputProps} ref={ref as ForwardedRef<HTMLInputElement>} />
})
